/* Feb-2026 · Itemised monthly royalty statement pane.
 *
 * Renders the §2.6(a)-(e) cost buckets × platform (Crown & Rose,
 * Amazon KDP, Google Play Books) for one month. Used inside
 * `MyRoyaltiesDialog` as an "Itemised" tab.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, FileText } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function _defaultPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function ItemisedRoyaltyPane() {
  const { format } = useCurrency();
  const [period, setPeriod] = useState(_defaultPeriod());
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(true);
  const [err,  setErr]      = useState(null);

  useEffect(() => {
    setBusy(true); setErr(null);
    fetch(`${API}/royalties/monthly/${period}/itemised`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
      .then(setData)
      .catch((e) => setErr(e.message || 'Failed to load statement.'))
      .finally(() => setBusy(false));
  }, [period]);

  const usd = (n) => format ? format(Math.round((n || 0) * 100)) : `$${Number(n || 0).toFixed(2)}`;
  const periodLabel = useMemo(() => {
    try {
      const [y, m] = period.split('-');
      return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    } catch { return period; }
  }, [period]);

  return (
    <div className="space-y-3" data-testid="itemised-royalty-pane">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <FileText className="h-4 w-4 text-primary" />
          Itemised statement · {periodLabel}
        </div>
        <label className="ml-auto flex items-center gap-2 text-[11px]">
          Period
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-7 rounded-sm border border-border bg-background px-2 text-[11.5px]"
            data-testid="itemised-period-input"
          />
        </label>
      </div>

      {busy && (
        <div className="py-8 text-center text-[12px] text-muted-foreground">
          <Loader2 className="mx-auto h-4 w-4 animate-spin" />
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3 text-[12px] text-rose-700">
          {err}
        </div>
      )}

      {!busy && !err && data && (
        <>
          <div className="rounded-md border border-border overflow-hidden">
            <table className="w-full text-[11.5px]">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5 text-left">Cost bucket</th>
                  {data.platforms.map((p) => (
                    <th key={p.key} className="px-2 py-1.5 text-right">{p.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-border/60">
                  <td className="px-2 py-1.5 font-semibold">Gross revenue</td>
                  {data.platforms.map((p) => (
                    <td key={p.key} className="px-2 py-1.5 text-right font-mono-stat"
                        data-testid={`itemised-gross-${p.key}`}>{usd(p.gross_usd)}</td>
                  ))}
                </tr>
                <tr className="border-t border-border/60">
                  <td className="px-2 py-1.5 text-muted-foreground">Author share (pre-cost)</td>
                  {data.platforms.map((p) => (
                    <td key={p.key} className="px-2 py-1.5 text-right font-mono-stat text-muted-foreground">
                      {usd(p.author_share_usd)}
                    </td>
                  ))}
                </tr>
                {(data.platforms[0]?.buckets || []).map((b, i) => (
                  <tr key={b.key} className="border-t border-border/60">
                    <td className="px-2 py-1.5 text-muted-foreground">{b.label}</td>
                    {data.platforms.map((p) => (
                      <td key={p.key} className="px-2 py-1.5 text-right font-mono-stat text-rose-600"
                          data-testid={`itemised-bucket-${b.key}-${p.key}`}>
                        {p.buckets[i]?.rate_pct
                          ? <span title={`${p.buckets[i].rate_pct}% of gross`}>–{usd(p.buckets[i].amount_usd)}</span>
                          : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-t border-border bg-emerald-500/5">
                  <td className="px-2 py-1.5 font-semibold text-emerald-700">Net to author</td>
                  {data.platforms.map((p) => (
                    <td key={p.key} className="px-2 py-1.5 text-right font-mono-stat font-semibold text-emerald-700"
                        data-testid={`itemised-net-${p.key}`}>
                      {usd(p.net_to_author_usd)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="rounded-md border border-border bg-card px-3 py-2 text-[11px]">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Statement total (all platforms)</span>
              <span className="font-mono-stat text-[13px] font-semibold" data-testid="itemised-total-net">
                {usd(data.totals.net_to_author_usd)}
              </span>
            </div>
            <div className="mt-0.5 flex items-center justify-between text-muted-foreground">
              <span>Gross across platforms</span>
              <span className="font-mono-stat">{usd(data.totals.gross_usd)}</span>
            </div>
          </div>

          <details className="rounded-md border border-border bg-card px-3 py-2 text-[11px]">
            <summary className="cursor-pointer text-muted-foreground">
              Legend · §2.6 cost buckets
            </summary>
            <ul className="mt-2 space-y-0.5">
              {(data.bucket_legend || []).map((b) => (
                <li key={b.key}>{b.label}</li>
              ))}
            </ul>
            <p className="mt-2 text-muted-foreground">
              Amazon KDP and Google Play Books rows populate once monthly
              royalty CSV imports are wired. Until then their gross shows $0 —
              your Crown &amp; Rose earnings above are always live.
            </p>
          </details>
        </>
      )}
    </div>
  );
}
